import { useEffect, useRef, useState } from 'react';

import { ButtonToggle } from '@peacecover/component';
import { ChevronDown } from '@peacecover/icon';
import { t } from 'i18next';
import { isEmpty, isUndefined } from 'lodash';

import saveData from '@pocapan/shared/utils/saveData';
import { useArtistQueryAll } from '../../../components/api/queries/useArtistQuery';

interface Artist {
  artistId: string;
  name: string;
  shortName: string | null;
  likedDate: string | null;
}

interface ArtistDropdownProps {
  onSelected?: (artist: Artist | null) => void;
}

const ArtistDropdown = ({ onSelected }: ArtistDropdownProps) => {
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const artists: Artist[] | undefined = useArtistQueryAll();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (artist: Artist) => {
    saveData('artist', artist.artistId);
    setSelectedArtist(artist);
    onSelected?.(artist);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    setSelectedArtist(null);
    onSelected?.(null);
    setIsOpen(false);
  };

  if (isUndefined(artists)) {
    return (
      <div className="h-[33px] w-[80px] rounded-full bg-surface-light dark:bg-surface-dark animate-pulse" />
    );
  }

  const likedArtists = artists.filter((a) => !!a.likedDate);

  if (isEmpty(likedArtists)) return null;

  const isAll = selectedArtist === null;
  const selectedLabel = isAll ? '아티스트 전체' : (selectedArtist.shortName || selectedArtist.name);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-[4px] px-[12px] py-[8px] rounded-full ${
          isAll
            ? 'bg-background-elevated ring-1 ring-inset ring-separator1'
            : 'bg-secondary-light dark:bg-secondary-dark'
        }`}
      >
        <span
          className={`text-[14px] font-semibold leading-[17px] truncate max-w-[100px] ${
            isAll ? 'text-text-main-lv2' : 'text-on-secondary'
          }`}
        >
          {selectedLabel}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${
            isAll ? 'text-text-main-lv2' : 'text-on-secondary'
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-[6px] bg-background-elevated rounded-[12px] shadow-[0_0_10px_0_rgba(0,0,0,0.04)] z-50 py-[12px] max-h-[200px] overflow-y-auto">
          <button
            type="button"
            onClick={handleSelectAll}
            className="flex items-center gap-[6px] pl-[12px] pr-[24px] py-[8px] w-full"
          >
            <ButtonToggle type="check" size="sm" checked={isAll} />
            <span
              className={`text-[14px] font-medium leading-[17px] text-nowrap truncate ${
                isAll ? 'text-primary-light dark:text-primary-dark' : 'text-text-main-lv4'
              }`}
            >
              {t('ArtistDropdown.entireArtist')}
            </span>
          </button>
          {likedArtists.map((artist) => (
            <button
              key={artist.artistId}
              type="button"
              onClick={() => handleSelect(artist)}
              className="flex items-center gap-[6px] pl-[12px] pr-[24px] py-[8px] w-full"
            >
              <ButtonToggle
                type="check"
                size="sm"
                checked={selectedArtist?.artistId === artist.artistId}
              />
              <span
                className={`text-[14px] font-medium leading-[17px] text-nowrap truncate ${
                  selectedArtist?.artistId === artist.artistId
                    ? 'text-primary-light dark:text-primary-dark'
                    : 'text-text-main-lv4'
                }`}
              >
                {artist.shortName || artist.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtistDropdown;
